import React, { useCallback, useContext, useMemo, useRef } from "react";
import { connect } from "dva";
import { useTranslation } from "react-i18next";
import { handleLinkClick } from "./utils";
import ComNoticeCard from "@/baseComponents/ComNoticeCard";
import { FocusSDK, isFocusEnv } from "@/utils";
import { changeEgovUrl } from "@/utils/tools";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import { Input, message as toast } from "antd";
import DeeplinkEvent from "@/components/DeeplinkHandler/DeeplinkEvent";
import ImService from "@/server/ImService";
import { commAICardButton } from "@/api/chat";
import { convertEmployeeToString } from "@/utils/chat/message";
import { parseUidFromString } from "@jd/jdee.im.sdk/lib/es/utils/utils";
import IconFont from "@/components/icon";
import { syncConfirmNew } from "@/utils/syncConfirm";
import { genThirdAppUrl } from "@/components/ThirdApp";

enum ButtonType {
  LINK = "link",
  BUTTON = "button"
}
function UnifiedCommCard({ message }: { message: any }) {
  const { authInfo, appRuntimeEnv } = React.useContext<GlobalContextConfig>(GlobalContext);
  const { currentEmployee } = useContext<GlobalContextConfig>(GlobalContext);
  const { title, extend = {}, sender, infox = {} } = message;
  const content = extend.fields;
  const avatarSrc = sender.avatar;
  const inputValueRef = useRef("");
  const [t] = useTranslation("chat");
  // 查阅类卡片
  const isReadCard = useMemo(() => {
    return extend?.showReadButton === true;
  }, [extend]);

  const isRead = useMemo(() => {
    return (message?.dealStatus & 4) === 4;
  }, [message]);

  // 查看链接
  const handleClickLink = useCallback(
    (url: string) => {
      const parseUrl = DeeplinkEvent.parseDeeplink(url);
      if (!parseUrl) {
        toast.warn("当前跳转链接不符合规范");
        return;
      }
      if (parseUrl.pathname && parseUrl.pathname.includes("browser")) {
        const isPrivateNet = FocusSDK.isPrivateNet();
        const mparam = parseUrl.mparam || {};
        let realUrl = isPrivateNet ? mparam.egovUrl : mparam.url;
        if (!realUrl) {
          toast.warn("该应用不支持在当前网络环境下打开");
          return;
        }
        const applicationKey = extend?.applicationKey || infox?.applicationKey;
        if (applicationKey) {
          realUrl = genThirdAppUrl(
            applicationKey,
            authInfo.ddAppId,
            authInfo.accessToken,
            appRuntimeEnv,
            {
              redirectUrl: realUrl
            }
          );
        }
        window.open(realUrl);
      } else {
        DeeplinkEvent.open(url);
      }
    },
    [
      appRuntimeEnv,
      authInfo?.accessToken,
      authInfo?.ddAppId,
      extend?.applicationKey,
      infox?.applicationKey
    ]
  );

  // 已阅
  const handleClickDeal = useCallback(() => {
    if (isRead) {
      return;
    }
    ImService.getInstance()?.cardDealMessage(message, 4);
  }, [message, isRead]);

  const confirmReply = useCallback(async () => {
    return await syncConfirmNew(
      <div>
        <Input.TextArea
          rows={4}
          onChange={({ target: { value } }) => {
            inputValueRef.current = value;
          }}
          maxLength={100}
        />
      </div>,
      "请填写原因"
    );
  }, []);

  const handleClick = useCallback(async (btn: any) => {
      if (btn.btnType === ButtonType.LINK && btn.linkParams?.pcUrl) {
        handleClickLink(btn.linkParams.pcUrl);
      } else if (btn.btnType === ButtonType.BUTTON && btn.buttonParams) {
        let replyInfo = "";
        if (btn?.btnReply) {
          const res = await confirmReply();
          if (res) {
            replyInfo = inputValueRef.current;
            inputValueRef.current = "";
          } else {
            inputValueRef.current = "";
            return;
          }
        }
        // 统一调用
        const cardRes = await commAICardButton({
          appUser: message.from,
          operator: parseUidFromString(convertEmployeeToString(currentEmployee)),
          action: btn.action,
          messageId: message.id,
          replyInfo,
          buttonParams: btn.buttonParams
        });
      }
    },
    [confirmReply, currentEmployee, handleClickLink, message.from, message.id]
  );

  // 加载按钮
  const loadButtons = useCallback(() => {
    if (isReadCard) {
      return (
        <div className={`button_item ${isRead ? "disable" : ""}`} onClick={() => handleClickDeal()}>
          {isRead && (
            <IconFont type="iconchenggong" className="icon" style={{ color: "#00A645" }} />
          )}
          {isRead ? t("ai-card-is-read") : t("ai-card-read")}
        </div>
      );
    } else {
      return extend?.btns?.length ? (
        <div className="button_items">
          {extend?.btns.map((btn: any, idx: number) => {
            const { statusIcon, statusColor, status } = btn;
            return (
              <button
                className={btn.status === "0" ? "disable" : ""}
                disabled={btn.status === "0" ? true : false}
                key={`${message.id}_${idx}`}
                onClick={() => handleClick(btn)}
              >
                {status === "0" && statusIcon && statusColor && (
                  <IconFont className="icon" type={statusIcon} style={{ color: statusColor }} />
                )}
                {`${btn.name}`}
              </button>
            );
          })}
        </div>
      ) : null;
    }
  }, [extend.btns, handleClick, handleClickDeal, isRead, isReadCard, message.id, t]);

  // 详情
  const handleDetail = useCallback(() => {
    // 已阅类
    if (isReadCard) {
      handleClickDeal();
    }
    handleClickLink(message?.infox?.deepLink);
  }, [handleClickLink, handleClickDeal, message?.infox?.deepLink, isReadCard]);

  return (
    <ComNoticeCard
      message={message}
      avatarSrc={changeEgovUrl(avatarSrc)}
      type={extend.applicationName}
      contentList={content}
      title={title}
      loadButtonsFn={loadButtons}
      handleDetail={message?.infox?.deepLink ? handleDetail : null}
    />
  );
}

export default UnifiedCommCard;
